import React, { useState, useMemo, useEffect } from "react";
import { Table, List, ChevronLeft, ChevronRight, Loader2, ArrowUpDown } from "lucide-react";
import DataTable from "./DataTable";
import ExpandableChargeableHours from "./ExpandableChargeableHours";
import DashboardTabHeader from "./shared/DashboardTabHeader";
import { PAGE_SIZE } from "./SortableTable";

// Adapted from the legacy component: analysis is computed client-side from the
// monthly aggregate records ({userName, position, month, hours, isAudit}) and
// the date range filter is a month range (startMonth / endMonth).
const ChargeableHoursDetails = ({ data, summary, dateRange, onDateRangeChange, loading }) => {
  const [viewMode, setViewMode] = useState("expandable"); // "expandable" or "classic"
  const [auditSearchTerm, setAuditSearchTerm] = useState("");
  const [auditSortOrder, setAuditSortOrder] = useState("desc"); // "desc" = highest audit % first
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = PAGE_SIZE; // one page size for the whole app

  const filteredData = useMemo(() => data || [], [data]);

  // Compute per-employee audit vs non-audit analysis from the monthly records
  const analysis = useMemo(() => {
    const byEmployee = new Map();
    let totalHours = 0;

    filteredData.forEach((r) => {
      const name = r.userName || "Unknown";
      const hours = Number(r.hours) || 0;
      if (!byEmployee.has(name)) {
        byEmployee.set(name, { name, position: r.position || "Unknown", auditHours: 0, nonAuditHours: 0, totalHours: 0 });
      }
      const emp = byEmployee.get(name);
      if (r.isAudit) emp.auditHours += hours;
      else emp.nonAuditHours += hours;
      emp.totalHours += hours;
      totalHours += hours;
    });

    const auditVsNonAudit = Array.from(byEmployee.values()).map((emp) => ({
      ...emp,
      auditPercentage: emp.totalHours > 0 ? (emp.auditHours / emp.totalHours) * 100 : 0,
      nonAuditPercentage: emp.totalHours > 0 ? (emp.nonAuditHours / emp.totalHours) * 100 : 0,
    }));

    const topNonAuditStaff = auditVsNonAudit
      .filter((emp) => emp.totalHours > 0)
      .sort((a, b) => b.nonAuditPercentage - a.nonAuditPercentage)
      .slice(0, 10);

    return {
      totalEmployees: byEmployee.size,
      totalHours,
      auditVsNonAudit,
      topNonAuditStaff,
    };
  }, [filteredData]);

  // Filter and paginate audit vs non-audit data
  const paginatedAuditData = useMemo(() => {
    let filtered = analysis.auditVsNonAudit || [];

    // Apply search filter
    if (auditSearchTerm.trim()) {
      const term = auditSearchTerm.toLowerCase();
      filtered = filtered.filter((emp) =>
        emp.name.toLowerCase().includes(term) ||
        (emp.position || '').toLowerCase().includes(term)
      );
    }

    // Sort by audit percentage
    filtered = filtered.sort((a, b) =>
      auditSortOrder === "desc" ? b.auditPercentage - a.auditPercentage : a.auditPercentage - b.auditPercentage
    );

    // Calculate pagination
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filtered.slice(startIndex, endIndex);

    return {
      data: paginatedData,
      totalItems: filtered.length,
      totalPages,
      currentPage,
      startIndex: startIndex + 1,
      endIndex: Math.min(endIndex, filtered.length),
    };
  }, [analysis, auditSearchTerm, auditSortOrder, currentPage, itemsPerPage]);

  // Reset to page 1 when search term or sort order changes
  useEffect(() => {
    setCurrentPage(1);
  }, [auditSearchTerm, auditSortOrder]);

  // Rows for the classic DataTable view — expose month/hours/type via `data`
  const tableData = useMemo(() => filteredData.map((r, idx) => ({
    id: `ch-${idx}`,
    userName: r.userName,
    position: r.position,
    hours: r.hours,
    data: {
      Month: r.month,
      Hours: r.hours,
      Type: r.isAudit ? 'Audit' : 'Non-Audit',
    },
  })), [filteredData]);

  return (
    <div className="space-y-6">
      {/* Month Range Picker */}
      {onDateRangeChange && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium text-gray-600">Month Range</span>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2">
                <label className="text-xs text-gray-500 font-medium">From</label>
                <input
                  type="month"
                  value={dateRange?.startMonth || ''}
                  onChange={(e) => onDateRangeChange(prev => ({ ...prev, startMonth: e.target.value }))}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-xs text-gray-500 font-medium">To</label>
                <input
                  type="month"
                  value={dateRange?.endMonth || ''}
                  onChange={(e) => onDateRangeChange(prev => ({ ...prev, endMonth: e.target.value }))}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              {(dateRange?.startMonth || dateRange?.endMonth) && (
                <button
                  onClick={() => onDateRangeChange({ startMonth: '', endMonth: '' })}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear
                </button>
              )}
            </div>
            {loading && (
              <div className="flex items-center space-x-2 text-blue-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading...</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty state */}
      {(!filteredData || filteredData.length === 0) ? (
        <div className="flex flex-col items-center justify-center py-12 px-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Chargeable Hours Data</h3>
          <p className="text-sm text-gray-600 text-center max-w-md">
            No data for the selected period.
          </p>
        </div>
      ) : (
      <>
      {/* Header — unified tab header (docs/dashboard-ui.md); the view toggle is
          this tab's only header action */}
      <DashboardTabHeader
        actions={
          <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode("expandable")}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === "expandable"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <List size={16} />
            <span>Expandable View</span>
          </button>
          <button
            onClick={() => setViewMode("classic")}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === "classic"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <Table size={16} />
            <span>Classic View</span>
          </button>
          </div>
        }
      />

      {/* Expandable Table View */}
      {viewMode === "expandable" && (
        <ExpandableChargeableHours data={filteredData} dateRange={dateRange} />
      )}

      {/* Classic Analytics View */}
      {viewMode === "classic" && (
        <>
      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">
                Employees
              </p>
              <p className="text-2xl font-bold text-green-900">
                {summary?.uniqueEmployees ?? analysis.totalEmployees ?? 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total Hours</p>
              <p className="text-2xl font-bold text-blue-900">
                {Math.round(summary?.totalHours ?? analysis.totalHours ?? 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-indigo-600">Audit Hours</p>
              <p className="text-2xl font-bold text-indigo-900">
                {Math.round(summary?.auditHours ?? 0)}
              </p>
              <p className="text-xs text-indigo-500">
                {(summary?.totalHours || 0) > 0 ? Math.round(summary?.auditPercentage ?? 0) + "%" : "—"}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-orange-600">Non-audit Hours</p>
              <p className="text-2xl font-bold text-orange-900">
                {Math.round(summary?.nonAuditHours ?? 0)}
              </p>
              <p className="text-xs text-orange-500">
                {(summary?.totalHours || 0) > 0 ? Math.round(summary?.nonAuditPercentage ?? 0) + "%" : "—"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Audit vs Non-Audit Analysis */}
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Audit vs Non-audit Hours Distribution
            </h3>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setAuditSortOrder(o => o === "desc" ? "asc" : "desc")}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700"
                title={auditSortOrder === "desc" ? "Sorted: highest audit % first" : "Sorted: lowest audit % first"}
              >
                <ArrowUpDown className="h-4 w-4" />
                <span>{auditSortOrder === "desc" ? "Desc" : "Asc"}</span>
              </button>
              <input
                type="text"
                placeholder="Search staff..."
                value={auditSearchTerm}
                onChange={(e) => setAuditSearchTerm(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48"
              />
              {auditSearchTerm && (
                <button
                  onClick={() => setAuditSearchTerm('')}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Results info */}
          <div className="text-sm text-gray-600 mb-3">
            Showing {paginatedAuditData.startIndex}-{paginatedAuditData.endIndex} of {paginatedAuditData.totalItems} staff
          </div>

          {/* Staff list */}
          <div className="space-y-2 mb-4">
            {paginatedAuditData.data.length > 0 ? (
              paginatedAuditData.data.map((emp, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="w-32 text-sm font-medium text-gray-700 truncate">
                    {emp.name}
                  </div>
                  <div className="flex-1 flex bg-gray-200 rounded-full h-6 overflow-hidden">
                    <div
                      className="bg-blue-500 flex items-center justify-center text-xs text-white font-medium"
                      style={{ width: `${emp.auditPercentage}%` }}
                    >
                      {emp.auditPercentage > 15
                        ? `${Math.round(emp.auditPercentage)}% Audit`
                        : ""}
                    </div>
                    <div
                      className="bg-orange-500 flex items-center justify-center text-xs text-white font-medium"
                      style={{ width: `${emp.nonAuditPercentage}%` }}
                    >
                      {emp.nonAuditPercentage > 15
                        ? `${Math.round(emp.nonAuditPercentage)}% Non-audit`
                        : ""}
                    </div>
                  </div>
                  <div className="w-16 text-sm text-gray-600">
                    {Math.round(emp.totalHours)}h
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No staff found matching "{auditSearchTerm}"
              </div>
            )}
          </div>

          {/* Pagination */}
          {paginatedAuditData.totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Previous</span>
              </button>

              <div className="flex items-center space-x-2">
                {Array.from({ length: paginatedAuditData.totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    // Show first, last, current, and pages around current
                    return page === 1 ||
                           page === paginatedAuditData.totalPages ||
                           Math.abs(page - currentPage) <= 1;
                  })
                  .map((page, idx, arr) => (
                    <React.Fragment key={page}>
                      {idx > 0 && arr[idx - 1] !== page - 1 && (
                        <span className="text-gray-400">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                          currentPage === page
                            ? 'bg-blue-500 text-white'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}
              </div>

              <button
                onClick={() => setCurrentPage(Math.min(paginatedAuditData.totalPages, currentPage + 1))}
                disabled={currentPage === paginatedAuditData.totalPages}
                className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Next</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Top Non-Audit Staff */}
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Top 10 Staff with Highest % Non-audit Hours
          </h3>
          <div className="space-y-2">
            {(analysis.topNonAuditStaff || []).map((emp) => (
              <div key={emp.name} className="flex items-center space-x-4">
                <div className="w-32 text-sm font-medium text-gray-700 truncate">{emp.name}</div>
                <div className="flex-1 bg-gray-200 rounded-full h-6 overflow-hidden">
                  <div
                    className="bg-orange-500 h-6 flex items-center justify-end pr-2 text-xs text-white font-medium"
                    style={{ width: `${Math.min(100, emp.nonAuditPercentage)}%` }}
                  >
                    {emp.nonAuditPercentage > 10 ? `${Math.round(emp.nonAuditPercentage)}%` : ''}
                  </div>
                </div>
                <div className="w-16 text-sm text-gray-600">{Math.round(emp.totalHours)}h</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">
            Chargeable Hours Details
          </h2>
          <div className="text-sm text-gray-500">
            Showing {tableData.length} of {filteredData.length} records
          </div>
        </div>

        <DataTable data={tableData} category="chargeableHours" />
      </div>
        </>
      )}
      </>
      )}
    </div>
  );
};

export default ChargeableHoursDetails;
